import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Store } from 'types/store';
import useCosmosTrackEvent from 'hooks/useCosmosTrackEvent';
import { setRefreshVehiclesList } from 'actions/content';
import {
  ClaimLoyaltyReward,
  GetLoyaltyRewards,
} from 'services/MyRacService';
import mobileAppEvent from 'utils/reactNativeWebView';
import {
  setRLRPageModel,
  setVehicleDisplayModality,
  setRLRVehicles,
  setRLRJourneyReward,
  setRLRJourneyError,
  setClaimRewardResponse,
} from 'actions/journeys/loyaltyRewardsActions';
import {
  RENEWAL,
  RENEWAL_ERROR,
  RAC_LOYALTY_REWARDS_CONFIRMATION,
} from 'constants/routeConstants';
import {
  VehicleFuelEnum,
  VehicleTransmissionEnum,
} from 'constants/vehicles';
import {
  RLRProductType,
  RLRVehicleInfoEnum,
  RLRProductCodesEnum,
  RLRVehicleInfoEnumType,
} from 'constants/journeys/loyaltyRewardsEnums';
import {
  ClaimRewardError,
  RLRClaimResponse,
  RLRJourneyDetails,
  RLROfferPayload,
  Reward,
  VehicleClaimModel,
} from 'types/journeys/loyaltyRewards';
import trackEvent from 'services/AnalyticsService';
import productsCopy from 'views/MyRac/Journeys/LoyaltyRewards/ProductsCopy.json';

const useLoyaltyRewards = (
  rlrJourneyDetails: RLRJourneyDetails | undefined,
  setLoading: (_value: boolean) => void,
  setClaimingReward: (_value: boolean) => void,
  setClaimRewardError: (_error: ClaimRewardError | null) => void,
  setIpiDocumentUrl: (_url: string | null) => void,
  setProducts: (_products: RLRProductType[]) => void,
  setIsExpiryJourney: (_value: boolean) => void,
) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAppView } = useSelector((state: Store) => state.content.flags);
  const surname = useSelector((state: Store) => state.customer.custInfo.Surname);
  const { memberNo, uid } = useSelector((state: Store) => state.session);
  const vehiclesState = useSelector((state: Store) => state.journeys.loyaltyRewards.vehicles);
  const vehicleDisplayModality = useSelector((state: Store) => state.journeys.loyaltyRewards.vehicleDisplayModality);
  const { selectedReward, chosenVehicle } = useSelector((state: Store) => state.journeys.loyaltyRewards.data);
  const { cosmosTrackEvent } = useCosmosTrackEvent('Loyalty Rewards');

  /**
   * Current implementation gives us policy vehicle as first item in the vehicle array when modality is
   * HasVerifiedExistingPolicyVehicle or OptionallyChangeExistingPolicyVehicle.
   * In case of HasVerifiedExistingPolicyVehicle we have just one vehicle in the array so it's the only vehicle that is selected
   * and it's not possible to change it.
   * In case of OptionallyChangeExistingPolicyVehicle we check if the chosenVehicle is the same as first in the array with below function
   */
  const isSameAsPolicyVehicle = useCallback(
    (registration: string): boolean => {
      if (!vehiclesState || vehiclesState.length === 0) return false;

      return vehiclesState[0].registration.toLowerCase() === registration.toLowerCase();
    },
    [vehiclesState],
  );

  const fetchRLROffer = useCallback(async () => {
    dispatch(setRLRJourneyReward(null));

    if (rlrJourneyDetails?.offersAndClaims) {
      setIsExpiryJourney(true);
      dispatch(setRLRPageModel(rlrJourneyDetails.offersAndClaims));
      dispatch(setVehicleDisplayModality(rlrJourneyDetails.offersAndClaims.vehicleDetailsCaptureModality));

      const { vehicles } = rlrJourneyDetails;
      const useFirstVehicleOnly = vehicles.length > 0
        && rlrJourneyDetails.offersAndClaims.vehicleDetailsCaptureModality === RLRVehicleInfoEnum.HasVerifiedExistingPolicyVehicle;

      dispatch(setRLRVehicles(useFirstVehicleOnly ? [vehicles[0]] : vehicles));

      const productsInfo = productsCopy.filter((productFromCopy) => rlrJourneyDetails.offersAndClaims.rewards.some(
          (productFromAPI: Reward) => productFromAPI.rewardCode === productFromCopy.productCode,
        )) as RLRProductType[];

      setProducts(productsInfo);
      setIpiDocumentUrl(rlrJourneyDetails.ipiDocumentUrl);
      setLoading(false);
      return;
    }

    try {
      const response = (await GetLoyaltyRewards()) as RLROfferPayload;
      const loyaltyRewards = response?.payload;
      if (loyaltyRewards?.offersAndClaims?.rewards) {
        cosmosTrackEvent('rlr_journey_started');

        dispatch(setRLRPageModel(loyaltyRewards.offersAndClaims));
        dispatch(setVehicleDisplayModality(loyaltyRewards.offersAndClaims.vehicleDetailsCaptureModality));

        const { vehicles } = loyaltyRewards;
        const useFirstVehicleOnly = vehicles.length > 0
          && loyaltyRewards.offersAndClaims.vehicleDetailsCaptureModality === RLRVehicleInfoEnum.HasVerifiedExistingPolicyVehicle;

        dispatch(setRLRVehicles(useFirstVehicleOnly ? [vehicles[0]] : vehicles));

        const { rewards } = loyaltyRewards.offersAndClaims;
        const productsInfo = productsCopy.filter((productFromCopy) => rewards.some(
            (productFromAPI: Reward) => productFromAPI.rewardCode === productFromCopy.productCode,
          )) as RLRProductType[];

        setProducts(productsInfo);
        setIpiDocumentUrl(loyaltyRewards.ipiDocumentUrl);
        setLoading(false);
      }
    } catch {
      dispatch(setRLRJourneyError({
        type: 'Technical error',
        title: 'Sorry, we’re having trouble with your offer',
        description: 'We’re experiencing some technical difficulties. Try refreshing the page or come back later.<br /> Prefer to talk? Call us on <a href="tel:0330 159 8691">0330 159 8691</a>',
        messageType: 'ERROR',
      }));
      setLoading(false);
    }
  }, [
    dispatch,
    rlrJourneyDetails,
    uid,
    isAppView,
    setLoading,
    setIpiDocumentUrl,
    setProducts,
    setIsExpiryJourney,
  ]);

  const claimReward = useCallback(async (isExpiryJourney: boolean, sourceOfClaim: number) => {
    setClaimingReward(true);

    const isBatteryOrTyreReplace = selectedReward?.productCode === RLRProductCodesEnum.DRV_BATTERY || selectedReward?.productCode === RLRProductCodesEnum.DRV_TYRE;

    try {
      let nominatedVehicle: VehicleClaimModel = {
        nominateVehicle: false,
        registration: '',
        make: '',
        model: '',
        yearOfManufacture: 0,
        transmission: VehicleTransmissionEnum.Manual,
        fuelType: VehicleFuelEnum.Petrol,
      }

      /**
       * For OptionallyChangeExistingPolicyVehicle and AddNewPolicyVehicle we always nominate the chosen vehicle so its
       * registration reaches Drive on the retain (MYR-20542). Keeping the existing vehicle sends it unchanged (the BE emits
       * VehicleEdits with old == new registration); choosing a different vehicle sends the new one. Only
       * HasVerifiedExistingPolicyVehicle must NOT nominate - there Drive already resolves the single verified policy vehicle,
       * and nominating it would be rejected as incoherent by rlr-offer-api. The policy vehicle is always the first item in the
       * vehicle array, checked via isSameAsPolicyVehicle.
       */
      const modalityNotAllowedToNominate = ([RLRVehicleInfoEnum.HasVerifiedExistingPolicyVehicle] as RLRVehicleInfoEnumType[]).includes(vehicleDisplayModality);

      const canNominateVehicle = chosenVehicle
        && !(isSameAsPolicyVehicle(chosenVehicle.registration) && modalityNotAllowedToNominate);

      if (selectedReward && isBatteryOrTyreReplace && canNominateVehicle) {
        nominatedVehicle = {
          ...chosenVehicle,
          nominateVehicle: true,
        }
      }

      const response = await ClaimLoyaltyReward(
        selectedReward?.productCode,
        surname,
        memberNo,
        sourceOfClaim,
        nominatedVehicle,
      ) as { payload: RLRClaimResponse};

      if (response?.payload) {
        const { payload } = response;

        dispatch(setClaimRewardResponse(payload));

        if (
          !payload.sspBusinessWindowClosedWhenClaimAttempted
          && payload.loyaltyRewardOfferClaimed
          && payload.policyRewardEligibility === 'PolicyEligibleForRewards'
        ) {
          trackEvent(`MyRAC_RLR_Selected_${selectedReward?.title.replaceAll(' ', '')}`)

          if (isBatteryOrTyreReplace && !!chosenVehicle) {
            trackEvent('MyRAC_RLR_Vehicle_Added');
            dispatch(setRefreshVehiclesList(true));
          }

          // If user is accessing journey through app, the app will be informed if RLR was claimed
          if (isAppView) {
            mobileAppEvent('RLR_REDEEMED');
          }

          if (isExpiryJourney) {
            if (payload.rewardClaimedButNeedsReconciliation) {
              navigate(RENEWAL_ERROR, { replace: true });
            } else {
              navigate(RENEWAL);
            }
          } else {
            navigate(RAC_LOYALTY_REWARDS_CONFIRMATION);
          }
        } else {
          setClaimRewardError(payload);
        }
      }
    } catch {
      dispatch(setRLRJourneyError({
        type: 'Technical error',
        title: 'Sorry, we’re having trouble with your offer',
        description: 'We’re experiencing some technical difficulties. Try refreshing the page or come back later.<br /> Prefer to talk? Call us on <a href="tel:0330 159 8691">0330 159 8691</a>',
        messageType: 'ERROR',
      }));
      setClaimingReward(false);
    }
  }, [
    chosenVehicle,
    dispatch,
    memberNo,
    navigate,
    selectedReward,
    setClaimingReward,
    setClaimRewardError,
    surname,
  ]);

  return {
    fetchRLROffer,
    claimReward,
  };
};

export default useLoyaltyRewards;
